import { useQuery } from "@tanstack/react-query"
import { useNbrAuth } from "@/app/auth/useNbrAuth"
import { getPlatformPermissions } from "@/shared/api/rgb"
import {
  NbrPermission,
  canModerateDeposits,
  hasPermission,
  type NbrPermissionName,
} from "@/shared/api/rgbPermissions"

export const permissionsQueryKey = ["platform", "permissions"] as const

export type NbrPermissions = {
  isLoading: boolean
  permissions: string[]
  has: (permission: NbrPermissionName | string) => boolean
  canCreateEvents: boolean
  canModerateEvents: boolean
  canModerateDeposits: boolean
  canManageAssociations: boolean
  canViewEventAudit: boolean
  canViewDepositAudit: boolean
  canManageCreativeCatalog: boolean
}

/** Fetches `GET /platform/permissions` when authenticated; drives portal/admin nav gates. */
export const useNbrPermissions = (): NbrPermissions => {
  const auth = useNbrAuth()
  const query = useQuery({
    queryKey: permissionsQueryKey,
    queryFn: getPlatformPermissions,
    enabled: auth.isAuthenticated,
    staleTime: 60_000,
  })

  const permissions = query.data?.permissions ?? []

  return {
    isLoading: auth.isAuthenticated && query.isLoading,
    permissions,
    has: (permission) => hasPermission(permissions, permission),
    canCreateEvents: hasPermission(permissions, NbrPermission.EventsCreate),
    canModerateEvents: hasPermission(permissions, NbrPermission.EventsApprove),
    canModerateDeposits: canModerateDeposits(permissions),
    canManageAssociations: hasPermission(permissions, NbrPermission.AssociationsManage),
    canViewEventAudit: hasPermission(permissions, NbrPermission.AuditEventsView),
    canViewDepositAudit: hasPermission(permissions, NbrPermission.AuditDepositsView),
    canManageCreativeCatalog: hasPermission(permissions, NbrPermission.CreativeSelfManageCatalog),
  }
}
